/* ============================================================================
   BLURRD CRO — Cart tier progress bar  (assets/cro-cart-tier.js)
   REVISION 7, Sept 14 2026.

   WHAT THIS IS
   The bundle builder page has its own rich progress UI. Once a shopper leaves
   that page the ladder becomes invisible, so a cart sitting one item short of
   $12-off-each looks finished. This file replays the same ladder inside the
   theme cart drawer and on the cart page, directly under the free-shipping bar.

   WHY IT IS A STANDALONE FILE AND NOT LIQUID
   sections/cart-drawer.liquid is 63KB of theme code that Shopify re-renders
   through the Section Rendering API on every cart mutation. Editing it risks
   the whole drawer on every future theme update. This file only ever INSERTS a
   node and re-inserts it when the theme wipes it, so the worst failure mode is
   the bar not appearing.

   THE SHIP GATE
   TIERS_LIVE stays false until the three automatic discounts are ACTIVE.
   While false this file renders nothing at all — it must never promise a
   discount that checkout will not honour. Flip the const to true (or set
   window.CRO_TIERS_LIVE = true from the console to preview) the moment the
   discounts go live, and re-upload.

   THE LADDER (must stay identical to cro-bundle.js and to the three
   DiscountAutomaticBasic nodes; per ITEM, not per basket)
     2-4 eligible items   $5 off each
     5-7 eligible items   $8 off each
     8+  eligible items   $12 off each

   WHAT COUNTS AS AN ELIGIBLE ITEM
   Exactly the union of the three smart collections the discounts are scoped to,
   replicated below against /cart.js product_type + product_title so the number
   on screen can never disagree with the number Shopify discounts. Smart
   collection TITLE rules are case-insensitive, hence the uppercasing.
   Re-read the live rules before changing anything here:
     Bundle Builder: Gloves      326470140055
     Bundle Builder: Glove Cases 326470172823
     Bundle Builder: Headcovers  326470205591
   ==========================================================================*/
(function () {
  'use strict';

  /* ---- THE SHIP GATE. See header. ------------------------------------- */
  var TIERS_LIVE = false;

  var TIERS = [
    { min: 2, each: 5,  qty: '2 items',  off: '$5 each' },
    { min: 5, each: 8,  qty: '5 items',  off: '$8 each' },
    { min: 8, each: 12, qty: '8+ items', off: '$12 each' }
  ];
  var TOP_MIN = 8;            /* rail is full here; also the last tier's min */
  var STYLE_ID = 'cro-ct-style';
  var NODE_CLASS = 'cro-ct';

  /* Anchors, in priority order. [selector, position] where position is
     'first' (insert as the anchor's first child) — both anchors want the bar
     above the line items, matching where the theme puts its shipping bar. */
  var ANCHORS = [
    ['#CartDrawer .drawer__scrollable', 'first'],   /* drawer  */
    ['.cart__sidebar', 'first']                     /* /cart page */
  ];

  var state = { count: 0, fetching: false, queued: false, painting: false };

  /* ---------------------------------------------------------------- utils */
  function live() {
    return TIERS_LIVE || window.CRO_TIERS_LIVE === true;
  }

  function money(n) {
    return '$' + (Math.round(n * 100) / 100).toFixed(2).replace(/\.00$/, '');
  }

  /* Replicates the three smart collections. item is a /cart.js line. */
  function isEligible(item) {
    var type = String(item.product_type || '').trim();
    var title = String(item.product_title || '').toUpperCase();

    /* Shared exclusions on all three collections: never let a mystery pack or a
       pre-made bundle count, or the shopper double-dips a discount that is
       already baked into that product's price. */
    if (title.indexOf('MYSTERY') !== -1) return false;
    if (title.indexOf('BUNDLE') !== -1) return false;

    if (type === 'Golf Glove') return title.indexOf('PACK') !== -1;
    if (type === 'Glove Case') return true;
    if (type === 'Headcover') return title.indexOf('3-PACK') === -1;
    return false;
  }

  function countEligible(cart) {
    var n = 0, items = (cart && cart.items) || [];
    for (var i = 0; i < items.length; i++) {
      if (isEligible(items[i])) n += (items[i].quantity || 0);
    }
    return n;
  }

  /* Current tier for a count, or null below the entry threshold. */
  function tierFor(n) {
    var t = null;
    for (var i = 0; i < TIERS.length; i++) if (n >= TIERS[i].min) t = TIERS[i];
    return t;
  }

  function nextTier(n) {
    for (var i = 0; i < TIERS.length; i++) if (n < TIERS[i].min) return TIERS[i];
    return null;
  }

  /* One line of copy. Deliberately mirrors the builder's wording so the two
     surfaces read as the same mechanic, and it always names the REAL next
     reward — never "add 1 more to save more", which is false at 3, 4, 6 and 7
     items where one more item earns nothing. */
  function copyFor(n) {
    var cur = tierFor(n);
    var nxt = nextTier(n);
    if (!cur) {
      var need = TIERS[0].min - n;
      return '<strong>Add ' + need + ' eligible item' + (need === 1 ? '' : 's') +
             '</strong> to unlock ' + money(TIERS[0].each) + ' off each';
    }
    if (!nxt) {
      return '<strong>Best rate unlocked</strong> &middot; ' + money(cur.each) +
             ' off each item, saving ' + money(cur.each * n);
    }
    var more = nxt.min - n;
    return '<strong>' + money(cur.each) + ' off each</strong> unlocked &middot; ' +
           more + ' more item' + (more === 1 ? '' : 's') + ' &rarr; ' +
           money(nxt.each) + ' off each';
  }

  function fillPct(n) {
    var pct = (Math.min(n, TOP_MIN) / TOP_MIN) * 100;
    return Math.max(n > 0 ? 6 : 0, pct);   /* endowed progress: never a dead 0 */
  }

  /* ----------------------------------------------------------------- style */
  function injectStyle() {
    if (document.getElementById(STYLE_ID)) return;
    var s = document.createElement('style');
    s.id = STYLE_ID;
    s.textContent = [
      '.cro-ct{display:block;margin:0 0 16px;padding:12px 14px;border:1px solid rgba(var(--color-foreground),.12);border-radius:var(--rounded-block,8px);background:rgba(var(--color-foreground),.03);font-family:var(--font-body-family)}',
      '.cro-ct__text{margin:0 0 8px;font-size:13px;line-height:1.35;color:rgba(var(--color-foreground),.85)}',
      '.cro-ct__text strong{font-weight:600;color:rgb(var(--color-foreground))}',
      /* REVISION 7c: the rail carries the whole ladder, not just a fill. overflow must
         stay visible so the labels can hang below it, and the rail needs bottom margin
         to reserve that space inside the drawer. */
      '.cro-ct__rail{position:relative;height:6px;border-radius:6px;background:rgba(var(--color-foreground),.18);overflow:visible;margin-bottom:32px}',
      '.cro-ct__fill{position:absolute;left:0;top:0;bottom:0;width:0;border-radius:6px;background:rgb(var(--color-button-background));transition:width .4s ease-out}',
      '.cro-ct__mark{position:absolute;top:0;transform:translateX(-50%);display:flex;flex-direction:column;align-items:center}',
      '.cro-ct__tick{display:block;width:2px;height:6px;background:rgb(var(--color-background))}',
      '.cro-ct__ml{margin-top:5px;display:grid;text-align:center;white-space:nowrap;line-height:1.25}',
      '.cro-ct__ml .q{font-size:12px;font-weight:600;color:rgba(var(--color-foreground),.75)}',
      '.cro-ct__ml .o{font-size:12px;color:rgba(var(--color-foreground),.55)}',
      '.cro-ct__mark.is-reached .q{color:rgb(var(--color-foreground))}',
      '.cro-ct__mark.is-reached .o{color:rgb(var(--color-button-background));font-weight:700}',
      '.cro-ct__mark[data-at="8"] .cro-ct__ml{transform:translateX(-50%)}',
      '.cro-ct__mark[data-at="2"] .cro-ct__ml{transform:translateX(14%)}',
      '.cro-ct__fill.is-full{background:rgb(var(--color-button-background))}',
      '@media (prefers-reduced-motion: reduce){.cro-ct__fill{transition:none!important}}'
    ].join('');
    document.head.appendChild(s);
  }

  /* Every tier as a marker on the rail: the item count that unlocks it and the
     per-item discount it pays. The last marker sits on the rail's right edge, so its
     label is pulled left to land its right edge on the tick rather than hang off. */
  function markupFor(n) {
    var out = '';
    for (var i = 0; i < TIERS.length; i++) {
      var t = TIERS[i];
      var pos = (Math.min(t.min, TOP_MIN) / TOP_MIN) * 100;
      out += '<span class="cro-ct__mark' + (n >= t.min ? ' is-reached' : '') +
             '" data-at="' + t.min + '" style="left:' + pos + '%">' +
               '<span class="cro-ct__tick"></span>' +
               '<span class="cro-ct__ml"><span class="q">' + t.qty + '</span>' +
               '<span class="o">' + t.off + '</span></span>' +
             '</span>';
    }
    return out;
  }

  /* ------------------------------------------------------------------ view */
  function build(n) {
    var el = document.createElement('div');
    el.className = NODE_CLASS;
    el.setAttribute('role', 'status');
    el.setAttribute('aria-live', 'polite');

    var marks = markupFor(n);

    el.innerHTML =
      '<p class="cro-ct__text">' + copyFor(n) + '</p>' +
      '<div class="cro-ct__rail">' +
        '<span class="cro-ct__fill' + (n >= TOP_MIN ? ' is-full' : '') + '"></span>' +
        marks +
      '</div>';

    /* Width is set SYNCHRONOUSLY, not in a rAF or a timeout. The drawer is
       re-rendered by the theme on every cart mutation, so this node is rebuilt
       constantly and an entrance animation would replay each time anyway. More
       importantly, rAF and setTimeout are both throttled while the tab is
       hidden, which would leave the rail painted at 0 for a shopper who added
       to cart in a background tab. The transition still animates every in-place
       update, which is where the unlock moment actually lands. */
    var fill = el.querySelector('.cro-ct__fill');
    if (fill) fill.style.width = fillPct(n) + '%';

    return el;
  }

  /* Insert or update the bar at every anchor present on the page. Sets
     state.painting so our own DOM writes do not wake the MutationObserver and
     bounce straight back into another refresh. */
  function paint(n) {
    if (!live()) { removeAll(); return; }
    state.painting = true;

    for (var i = 0; i < ANCHORS.length; i++) {
      var host = document.querySelector(ANCHORS[i][0]);
      if (!host) continue;

      var existing = host.querySelector(':scope > .' + NODE_CLASS);

      if (existing) {
        /* Update in place so the rail animates instead of snapping. */
        var txt = existing.querySelector('.cro-ct__text');
        var fill = existing.querySelector('.cro-ct__fill');
        if (txt) txt.innerHTML = copyFor(n);
        if (fill) {
          fill.style.width = fillPct(n) + '%';
          fill.classList.toggle('is-full', n >= TOP_MIN);
        }
        /* Markers light up as tiers are reached, same as the builder's own bars. */
        var marks = existing.querySelectorAll('.cro-ct__mark');
        for (var k = 0; k < marks.length; k++) {
          var at = parseInt(marks[k].getAttribute('data-at'), 10);
          marks[k].classList.toggle('is-reached', n >= at);
        }
      } else {
        host.insertBefore(build(n), host.firstChild);
      }
    }

    state.painting = false;
  }

  function removeAll() {
    var nodes = document.querySelectorAll('.' + NODE_CLASS);
    for (var i = 0; i < nodes.length; i++) nodes[i].parentNode.removeChild(nodes[i]);
  }

  /* ------------------------------------------------------------------ data */
  function refresh() {
    if (!live()) { removeAll(); return; }
    if (state.fetching) { state.queued = true; return; }
    state.fetching = true;

    fetch(window.Shopify && window.Shopify.routes && window.Shopify.routes.root
            ? window.Shopify.routes.root + 'cart.js'
            : '/cart.js',
          { headers: { 'Accept': 'application/json' }, credentials: 'same-origin' })
      .then(function (r) { return r.ok ? r.json() : null; })
      .then(function (cart) {
        if (cart) { state.count = countEligible(cart); paint(state.count); }
      })
      .catch(function () { /* silent: a missing bar must never break the cart */ })
      .then(function () {
        state.fetching = false;
        if (state.queued) { state.queued = false; refresh(); }
      });
  }

  /* --------------------------------------------------------------- binding */
  var debounceId = null;
  function schedule() {
    clearTimeout(debounceId);
    debounceId = setTimeout(refresh, 150);
  }

  /* The theme re-renders the drawer through the Section Rendering API, which
     replaces the whole subtree and takes our node with it. Rather than hook a
     theme-specific event name (which a theme update can rename), watch the
     cart regions and re-paint whenever they change AND our node is gone, or
     whenever the item count element changes. Cheap, and theme-agnostic. */
  function observe() {
    if (!window.MutationObserver) return;
    var obs = new MutationObserver(function (records) {
      if (state.painting) return;
      for (var i = 0; i < records.length; i++) {
        var t = records[i].target;
        if (!t || !t.closest) continue;
        if (t.closest('.' + NODE_CLASS)) continue;   /* our own writes */
        if (t.closest('#CartDrawer') || t.closest('.cart__sidebar') ||
            t.closest('cart-count') || t.closest('.cart__items')) {
          schedule();
          return;
        }
      }
    });
    obs.observe(document.body, { childList: true, subtree: true, characterData: true });
  }

  function start() {
    if (!live()) return;             /* gate: render nothing, bind nothing */
    injectStyle();
    refresh();
    observe();
    /* Belt and braces for themes that do dispatch a cart event. */
    ['cart:refresh', 'cart:updated', 'cart:change', 'variant:added']
      .forEach(function (name) { document.addEventListener(name, schedule); });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start);
  } else {
    start();
  }

  /* Exposed so the gate can be flipped from the console for a live preview:
       window.CRO_TIERS_LIVE = true; window.CROCartTier.start();            */
  window.CROCartTier = { start: start, refresh: refresh, count: function () { return state.count; } };
})();
